import React from "react";
import { motion } from "framer-motion";
import {
    ArrowDown,
    ArrowRight,
    Sparkle,
    CursorClick,
} from "@phosphor-icons/react";
import MagneticHover from "./MagneticHover";

const fadeUp = {
    hidden: { y: 28, opacity: 0 },
    show: (i = 0) => ({
        y: 0,
        opacity: 1,
        transition: {
            duration: 0.85,
            delay: 0.08 + i * 0.07,
            ease: [0.22, 1, 0.36, 1],
        },
    }),
};

const stickerVariants = {
    hidden: { scale: 0.6, opacity: 0, y: 30 },
    show: (i = 0) => ({
        scale: 1,
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.7,
            delay: 0.7 + i * 0.1,
            ease: [0.34, 1.56, 0.64, 1],
        },
    }),
};

export default function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-[100svh] grain overflow-hidden bg-scd"
        >
            {/* Soft yellow blob — top right */}
            <div
                aria-hidden
                className="absolute top-[10%] right-[-6%] w-[40vw] h-[40vw] max-w-[560px] max-h-[560px] rounded-full"
                style={{
                    background:
                        "radial-gradient(circle at 30% 30%, #ffd140 0%, rgba(255,209,64,0.0) 65%)",
                    filter: "blur(40px)",
                    opacity: 0.55,
                }}
            />
            {/* Soft green blob — bottom left */}
            <div
                aria-hidden
                className="absolute bottom-[-15%] left-[-12%] w-[45vw] h-[45vw] max-w-[600px] max-h-[600px] rounded-full"
                style={{
                    background:
                        "radial-gradient(circle at 50% 50%, #3b5838 0%, rgba(59,88,56,0) 60%)",
                    filter: "blur(60px)",
                    opacity: 0.16,
                }}
            />

            {/* Decorative star — top left */}
            <motion.div
                initial={{ rotate: -20, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                transition={{ duration: 1.4, delay: 0.2 }}
                aria-hidden
                className="absolute top-[18%] left-[6%] md:left-[8%] hidden sm:block"
                data-testid="hero-deco-sparkle"
            >
                <Sparkle
                    size={56}
                    weight="fill"
                    className="text-yellow"
                    style={{
                        filter:
                            "drop-shadow(0 4px 16px rgba(255,209,64,0.45))",
                    }}
                />
            </motion.div>

            {/* Black pill badge — top right */}
            <motion.div
                initial={{ y: -30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.4 }}
                className="absolute top-[14%] right-[6%] md:right-[10%] hidden sm:flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#272727] text-[#faf7f1]"
                data-testid="hero-badge"
            >
                <span className="w-2 h-2 rounded-full bg-yellow animate-pulse" />
                <span className="font-display text-xs tracking-[0.18em] font-semibold">
                    +50 PROJETS
                </span>
            </motion.div>

            {/* Floating scroll-down arrow — right edge */}
            <motion.a
                href="#services"
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.7, delay: 1.2 }}
                aria-label="Défiler vers les services"
                data-testid="hero-scroll-arrow"
                className="hidden lg:flex absolute right-[3%] top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[#272727] text-[#faf7f1] items-center justify-center hover:bg-green transition-colors animate-bounce-slow"
            >
                <ArrowDown size={18} weight="bold" />
            </motion.a>

            <div className="relative z-10 px-6 md:px-12 lg:px-16 pt-32 md:pt-40 pb-16 flex flex-col items-center text-center min-h-[100svh] justify-center">
                {/* Eyebrow */}
                <motion.div
                    initial="hidden"
                    animate="show"
                    variants={fadeUp}
                    custom={0}
                    className="inline-flex items-center gap-2 mb-8"
                    data-testid="hero-eyebrow"
                >
                    <span className="label-eyebrow text-cream/75">
                        Studio multidisciplinaire — Québec
                    </span>
                </motion.div>

                {/* Headline */}
                <motion.h1
                    initial="hidden"
                    animate="show"
                    className="font-display font-semibold text-cream leading-[0.9] tracking-tight max-w-5xl"
                    style={{
                        fontSize: "clamp(3rem, 11vw, 9.5rem)",
                        letterSpacing: "-0.04em",
                    }}
                    data-testid="hero-headline"
                    aria-label="Élevez votre image — Studio créatif à Magog, Québec"
                >
                    {/* Accessible / SEO-friendly clean text (screen readers + crawlers) */}
                    <span className="sr-only">
                        Élevez votre image — Studio créatif à Magog, Québec.
                    </span>
                    {/* Visually animated version (hidden from assistive tech) */}
                    <span aria-hidden="true">
                        {"Élevez votre".split(" ").map((word, i) => (
                        <span
                            key={i}
                            className="inline-block overflow-hidden align-bottom mr-[0.18em]"
                        >
                            <motion.span
                                initial={{ y: "110%" }}
                                animate={{ y: 0 }}
                                transition={{
                                    duration: 0.95,
                                    delay: 0.18 + i * 0.08,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                className="inline-block"
                            >
                                {word}
                            </motion.span>
                        </span>
                    ))}
                    <br />
                    <span className="inline-block overflow-hidden align-bottom">
                        <motion.span
                            initial={{ y: "110%" }}
                            animate={{ y: 0 }}
                            transition={{
                                duration: 0.95,
                                delay: 0.4,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className="inline-block font-display italic text-green"
                        >
                            image.
                        </motion.span>
                    </span>
                    </span>
                </motion.h1>

                {/* Sub */}
                <motion.p
                    initial="hidden"
                    animate="show"
                    variants={fadeUp}
                    custom={3}
                    className="mt-8 md:mt-10 max-w-xl text-cream/75 text-lg md:text-xl leading-relaxed"
                >
                    Studio créatif qui propulse les petites entreprises avec une
                    image forte — <em>photo, vidéo, sites web et identité de
                    marque</em>.
                </motion.p>

                {/* Black CTA pill */}
                <motion.div
                    initial="hidden"
                    animate="show"
                    variants={fadeUp}
                    custom={4}
                    className="mt-10 md:mt-12 flex flex-col sm:flex-row items-center gap-4"
                >
                    <MagneticHover>
                        <a
                            href="#contact"
                            data-testid="hero-cta-primary"
                            className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#272727] text-[#faf7f1] font-semibold text-sm tracking-[0.1em] uppercase hover:bg-green transition-colors"
                        >
                            Démarrer mon projet
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-yellow text-[#272727] group-hover:translate-x-1 transition-transform">
                                <ArrowRight size={13} weight="bold" />
                            </span>
                        </a>
                    </MagneticHover>
                    <a
                        href="#showreel"
                        data-testid="hero-cta-secondary"
                        className="inline-flex items-center gap-2 px-6 py-3 text-cream/85 hover-underline label-eyebrow"
                    >
                        Voir notre travail
                    </a>
                </motion.div>

                {/* Sticker shapes row */}
                <div
                    className="relative mt-16 md:mt-24 w-full max-w-3xl flex flex-wrap items-center justify-center gap-3 md:gap-5"
                    data-testid="hero-stickers"
                >
                    <Sticker variant={0} index={0} rotate={-8}>
                        <span className="font-display tracking-tight text-lg md:text-xl">
                            PHOTO
                        </span>
                    </Sticker>
                    <Sticker variant={1} index={1} rotate={5}>
                        <span className="font-display tracking-tight text-lg md:text-xl">
                            VIDÉO
                        </span>
                    </Sticker>
                    <Sticker variant={2} index={2} rotate={-3}>
                        <span className="font-display tracking-tight text-lg md:text-xl">
                            WEB
                        </span>
                        <CursorClick size={16} weight="fill" />
                    </Sticker>
                    <Sticker variant={3} index={3} rotate={7}>
                        <span className="font-display tracking-tight text-lg md:text-xl">
                            BRANDING
                        </span>
                    </Sticker>
                </div>

                {/* Bottom stats — discreet */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 1.6 }}
                    className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 text-cream/55"
                >
                    <ArrowDown size={12} weight="bold" />
                    <span className="label-eyebrow text-[10px]">
                        Défilez pour découvrir
                    </span>
                </motion.div>
            </div>

            <style>{`
                @keyframes bounce-slow {
                    0%, 100% { transform: translate(0, -50%); }
                    50% { transform: translate(0, calc(-50% + 6px)); }
                }
                .animate-bounce-slow {
                    animation: bounce-slow 2.6s ease-in-out infinite;
                }
            `}</style>
        </section>
    );
}

/**
 * Sticker variants — 4 distinct sticker shapes / colours staying within the
 * brand palette (cream / dark / yellow / green).
 */
function Sticker({ variant, rotate = 0, index = 0, children }) {
    const styles = [
        // 0: Green pill
        "px-7 py-4 rounded-full bg-green text-[#faf7f1] shadow-[6px_6px_0_#272727]",
        // 1: Yellow circle
        "w-[120px] h-[120px] md:w-[140px] md:h-[140px] rounded-full bg-yellow text-[#272727] shadow-[6px_6px_0_#272727]",
        // 2: Dark square
        "px-7 py-5 rounded-md bg-[#272727] text-[#faf7f1] shadow-[6px_6px_0_#ffd140]",
        // 3: Cream pill with dark border
        "px-7 py-4 rounded-full bg-[#faf7f1] text-[#272727] border-2 border-[#272727] shadow-[6px_6px_0_#3b5838]",
    ];

    // Continuous gentle float — different params per sticker for organic feel
    const floatY = [0, -6, 0, -3, 0];
    const floatRotate = [rotate, rotate + 1.5, rotate - 1.2, rotate + 0.8, rotate];
    const floatDuration = 5 + (index % 3) * 0.7;

    return (
        <motion.div
            initial={{ scale: 0.6, opacity: 0, y: 30, rotate: rotate - 15 }}
            animate={{
                scale: 1,
                opacity: 1,
                y: floatY,
                rotate: floatRotate,
                transition: {
                    scale: { duration: 0.7, delay: 0.7 + index * 0.1, ease: [0.34, 1.56, 0.64, 1] },
                    opacity: { duration: 0.7, delay: 0.7 + index * 0.1 },
                    y: {
                        duration: floatDuration,
                        delay: 1.5 + index * 0.15,
                        repeat: Infinity,
                        ease: "easeInOut",
                    },
                    rotate: {
                        duration: floatDuration + 1,
                        delay: 1.5 + index * 0.15,
                        repeat: Infinity,
                        ease: "easeInOut",
                    },
                },
            }}
            whileHover={{
                scale: 1.08,
                rotate: rotate + (rotate >= 0 ? 6 : -6),
                transition: { duration: 0.3 },
            }}
            className={`inline-flex items-center justify-center gap-2 select-none cursor-pointer ${styles[variant]}`}
            data-testid={`hero-sticker-${variant}`}
        >
            {children}
        </motion.div>
    );
}
